import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PLANNING_STORAGE_KEY,
  PLANNING_TTL_MS,
  addPlanned,
  movePlanned,
  movePlannedTo,
  readStoredPlanning,
  removePlanned,
  writeStoredPlanning,
} from './planning';

// DJB-248 / DJB-249: page state for the Planning panel on Accounts › Maps.
// Holds the ordered machine ids, mirrors every change into localStorage
// (planning.js, 10 h TTL counted from the last change) and forgets the list
// when that TTL runs out — on the next load, or live if the tab stays open
// that long. Scope is the browser: every operator keeps their own planning.
export const usePlanning = () => {
  const [state, setState] = useState(() => readStoredPlanning());
  const { ids, savedAt } = state;

  // every change writes the list + its timestamp; an empty list removes the
  // entry. Writing what was just read back is idempotent, so no first-run guard.
  useEffect(() => {
    writeStoredPlanning(ids, savedAt);
  }, [ids, savedAt]);

  // live expiry while the page stays open
  useEffect(() => {
    if (!ids.length) return undefined;
    const delay = Math.max(0, savedAt + PLANNING_TTL_MS - Date.now());
    const timer = setTimeout(() => setState({ ids: [], savedAt: Date.now() }), delay);
    return () => clearTimeout(timer);
  }, [ids, savedAt]);

  // another tab of the same browser changed the planning → follow it
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === null || event.key === PLANNING_STORAGE_KEY) {
        setState(readStoredPlanning());
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const update = useCallback((change) => {
    setState((prev) => {
      const next = change(prev.ids);
      return next === prev.ids ? prev : { ids: next, savedAt: Date.now() };
    });
  }, []);

  const add = useCallback((machineId) => update((list) => addPlanned(list, machineId)), [update]);
  const remove = useCallback(
    (machineId) => update((list) => removePlanned(list, machineId)),
    [update]
  );
  const move = useCallback((from, to) => update((list) => movePlanned(list, from, to)), [update]);
  const moveTo = useCallback(
    (machineId, targetId, options) => update((list) => movePlannedTo(list, machineId, targetId, options)),
    [update]
  );
  const clear = useCallback(() => update((list) => (list.length ? [] : list)), [update]);

  const plannedIds = useMemo(() => new Set(ids), [ids]);

  return { ids, plannedIds, add, remove, move, moveTo, clear };
};

export default usePlanning;
